<script lang="ts" setup>
import { ref } from 'vue';
import { A11y } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Swiper as SwiperClass, SwiperModule } from 'swiper/types';
import { Icon } from '@iconify/vue';
import Container from '~/components/Container.vue';
import TimelineItem, { Item } from './Timeline/Item.vue';

interface Props {
  anchor?: string;
  title?: string;
  description?: string;
  items: Item[];
}

defineProps<Props>();

const modules: SwiperModule[] = [A11y];
const swiperUse = ref<SwiperClass | null>(null);

function onSwiper(swiper: SwiperClass) {
  swiperUse.value = swiper;
}

const swiperOptions = {
  init: false,
  wrapperTag: 'div',
  modules,
  slidesPerView: 'auto',
  freeMode: {
    enabled: true,
  },
  keyboard: {
    enabled: true,
    onlyInViewport: false,
  },
};
</script>

<template>
  <section v-if="items && items.length > 0" :id="anchor" class="timeline">
    <Container is-fluid>
      <div class="timeline__wrapper">
        <Container class="timeline__container">
          <div v-if="title || description" class="timeline__heading">
            <h2 v-if="title" class="timeline__title">{{ title }}</h2>
            <p v-if="description" class="timeline__description">{{ description }}</p>
          </div>
          <div class="timeline__slider">
            <Swiper v-bind="swiperOptions" @swiper="onSwiper">
              <SwiperSlide v-for="(item, index) in items" :key="`tiles-slider-item-${index}`">
                <TimelineItem :item="item" />
              </SwiperSlide>
            </Swiper>
            <button
              class="swiper-button-prev"
              :class="{
                'swiper-button-disabled': swiperUse?.isBeginning,
                'swiper-button-lock': swiperUse?.isLocked,
              }"
              @click="swiperUse?.slidePrev()"
            >
              <span class="sr-only">Prev Slide</span>
              <Icon icon="bi:arrow-left" />
            </button>
            <button
              class="swiper-button-next"
              :class="{
                'swiper-button-disabled': swiperUse?.isEnd,
                'swiper-button-lock': swiperUse?.isLocked,
              }"
              @click="swiperUse?.slideNext()"
            >
              <span class="sr-only">Next Slide</span>
              <Icon icon="bi:arrow-right" />
            </button>
          </div>
        </Container>
      </div>
    </Container>
  </section>
</template>

<style lang="scss" scoped>
.timeline {
  @apply my-10;
  @apply md:my-16;
  @apply xl:my-24;

  &__wrapper {
    @apply bg-light-accent rounded-10 overflow-hidden py-10;
    @apply md:py-16;
    @apply xl:rounded-30 xl:py-20;
  }

  &__heading {
    @apply flex flex-col items-center text-center mb-6 gap-6;
    @apply xl:mb-12 xl:gap-10;
  }

  &__title {
    @apply font-semibold text-[24px] leading-[1.1];
    @apply xl:text-[38px];
  }

  &__slider {
    @apply flex flex-wrap;
  }

  &__description {
    @apply font-medium leading-[1.3] text-[16px] text-base-color/80;
    @apply xl:text-[20px] xl:max-w-[60%] xl:mx-auto;
  }

  .swiper {
    @apply w-[calc(100%-1.5rem)] -ml-3 overflow-visible;

    &-button-prev,
    &-button-next {
      @apply static text-accent after:hidden translate-y-0 mt-10 mx-1 h-auto w-auto p-2 [&>svg]:w-10 duration-300 hover:text-dark;
    }

    &-button-prev {
      @apply ml-auto;
    }

    &-button-next {
      @apply mr-auto;
    }
  }

  :deep(.swiper-slide) {
    @apply w-4/5 px-3 min-h-[320px] flex first:before:rounded-l-full last:before:rounded-r-full;
    @apply before:h-[10px] before:absolute before:w-full before:top-1/2 before:left-0 before:-translate-y-1/2 before:bg-accent;
    @apply after:w-6 after:h-6 after:border-[4px] after:border-accent after:rounded-full after:bg-white after:absolute after:top-1/2 after:-translate-y-1/2 after:left-5;
    @apply md:w-1/2;
    @apply lg:w-1/3;
    @apply xl:w-1/4;
    @apply 2xl:w-1/5;
  }

  :deep(.swiper-slide:nth-child(2n) .timeline-item__wrapper) {
    @apply mt-auto;
  }

  :deep(.swiper-slide:nth-child(2n) .timeline-item) {
    @apply before:translate-y-0;
  }

  :deep(.swiper-slide:not(:first-child) .timeline-item__wrapper) {
    @apply w-[calc(100%+5rem)] -mx-10;
  }

  :deep(.swiper-slide:first-child .timeline-item__wrapper) {
    @apply w-[calc(100%+5rem)] -mr-20;
  }
}
</style>
